import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { ArrowRight, Clock, Check, MessageCircleQuestion } from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { AskQuestionModal } from "@/components/academy/AskQuestionModal";
import { TrackCard } from "@/components/academy/TrackCard";
import { getTrackBySlug, listTracks } from "@/lib/academy.functions";
import { fetchCompletedLessonIds, ensureSessionBootstrap } from "@/lib/academy-progress";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/academy/tracks/$slug")({
  loader: async ({ params, context }) => {
    const data = await context.queryClient.ensureQueryData({
      queryKey: ["academy", "track", params.slug],
      queryFn: () => getTrackBySlug({ data: { slug: params.slug } }),
    });
    if (!data) throw notFound();
    await context.queryClient.ensureQueryData({
      queryKey: ["academy", "tracks"],
      queryFn: () => listTracks(),
    });
  },
  head: ({ params }) => ({
    meta: [
      { title: `${params.slug.replace(/-/g, " ")} — Academy Track` },
      {
        name: "description",
        content: `Structured lessons in the ${params.slug.replace(/-/g, " ")} learning track.`,
      },
    ],
  }),
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-32 px-5 text-center">
        <h1 className="font-display text-3xl text-navy-900">Track not found</h1>
        <Link to="/academy" className="mt-4 inline-block text-bronze-600">
          ← Back to Academy
        </Link>
      </main>
      <Footer />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="p-10 font-sans text-charcoal-900">Error: {error.message}</div>
  ),
  component: TrackPage,
});

function TrackPage() {
  const { slug } = Route.useParams();
  const [askOpen, setAskOpen] = useState(false);

  useEffect(() => { ensureSessionBootstrap(); }, []);

  const { data } = useSuspenseQuery({
    queryKey: ["academy", "track", slug],
    queryFn: () => getTrackBySlug({ data: { slug } }),
  });
  const { data: allTracks } = useSuspenseQuery({
    queryKey: ["academy", "tracks"],
    queryFn: () => listTracks(),
  });
  const lessonIds = (data?.lessons ?? []).map((l) => l.id);
  const { data: completed } = useQuery({
    queryKey: ["academy", "progress", slug, lessonIds.length],
    queryFn: () => fetchCompletedLessonIds(lessonIds),
    enabled: lessonIds.length > 0,
  });

  if (!data) return null;
  const { track, lessons } = data;
  const totalMin = lessons.reduce((s, l) => s + (l.estimated_minutes ?? 0), 0);
  const completedSet = completed ?? new Set<string>();
  const completedCount = lessons.filter((l) => completedSet.has(l.id)).length;
  const pct = lessons.length ? Math.round((completedCount / lessons.length) * 100) : 0;
  const continueLesson =
    lessons.find((l) => !completedSet.has(l.id)) ?? lessons[0] ?? null;
  const related = allTracks.filter((t) => t.slug !== track.slug).slice(0, 4);

  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">
        <section className="bg-navy-900 text-cream-50">
          <div className="mx-auto max-w-6xl px-5 py-20 md:px-10 md:py-24">
            <Link
              to="/academy"
              className="font-sans text-[12px] uppercase tracking-[0.2em] text-bronze-400 hover:text-bronze-400/80"
            >
              ← The Academy
            </Link>
            <div className="mt-6">
              <Eyebrow color="cream">Learning Track</Eyebrow>
            </div>
            <h1 className="mt-6 font-display text-[44px] leading-[1.1] md:text-[56px]">
              {track.title}
            </h1>
            {track.subtitle && (
              <p className="mt-3 font-display text-[20px] italic text-bronze-400">
                {track.subtitle}
              </p>
            )}
            {track.description && (
              <p className="mt-6 max-w-2xl font-sans text-[17px] leading-relaxed text-cream-50/80">
                {track.description}
              </p>
            )}
            <p className="mt-8 font-sans text-[12px] uppercase tracking-[0.22em] text-cream-50/70">
              {lessons.length} lesson{lessons.length === 1 ? "" : "s"} · ~{totalMin} min
              {completedCount > 0 ? ` · ${completedCount} complete` : ""}
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-16 md:px-10">
          <div className="grid gap-10 lg:grid-cols-[1fr_320px]">
            {/* Timeline */}
            <div>
              <Eyebrow>Lessons in this Track</Eyebrow>
              {lessons.length === 0 ? (
                <p className="mt-8 font-sans text-charcoal-500">
                  Lessons coming soon for this track.
                </p>
              ) : (
                <ol className="mt-6 divide-y divide-bronze-600/20 border-y border-bronze-600/20">
                  {lessons.map((lesson, i) => {
                    const done = completedSet.has(lesson.id);
                    return (
                      <li key={lesson.id}>
                        <Link
                          to="/academy/tracks/$trackSlug/lessons/$lessonSlug"
                          params={{ trackSlug: track.slug, lessonSlug: lesson.slug }}
                          className="group flex items-start gap-6 py-6 transition-all hover:-translate-y-0.5 hover:bg-white/60 hover:shadow-[0_10px_30px_-20px_rgba(168,139,92,0.45)] px-3 -mx-3"
                        >
                          <span
                            className={cn(
                              "mt-1 flex h-10 w-10 shrink-0 items-center justify-center font-display text-[20px]",
                              done
                                ? "rounded-full bg-bronze-600 text-cream-50"
                                : "text-bronze-600",
                            )}
                          >
                            {done ? <Check className="h-5 w-5" strokeWidth={2.5} /> : String(i + 1).padStart(2, "0")}
                          </span>
                          <div className="flex-1">
                            <h3 className="font-display text-[22px] leading-snug text-navy-900">
                              {lesson.title}
                            </h3>
                            {lesson.excerpt && (
                              <p className="mt-1.5 font-sans text-[14px] leading-relaxed text-charcoal-500">
                                {lesson.excerpt}
                              </p>
                            )}
                            <div className="mt-2.5 flex items-center gap-4 font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">
                              <span className="border border-bronze-600/40 px-2 py-0.5">
                                {lesson.lesson_type}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {lesson.estimated_minutes} min
                              </span>
                            </div>
                          </div>
                          <ArrowRight className="mt-2 h-5 w-5 shrink-0 text-bronze-600 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>

            {/* Sticky sidebar */}
            <aside className="lg:sticky lg:top-28 lg:self-start">
              <div
                className="border border-bronze-600/30 bg-white p-6 shadow-[0_10px_30px_-20px_rgba(168,139,92,0.35)]"
                style={{ borderRadius: 6 }}
              >
                <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.22em] text-bronze-600">
                  {completedCount > 0 ? "Continue Learning" : "Begin Track"}
                </p>
                {continueLesson && (
                  <>
                    <h3 className="mt-3 font-display text-[20px] leading-tight text-navy-900">
                      {continueLesson.title}
                    </h3>
                    <p className="mt-2 font-sans text-[12px] uppercase tracking-[0.18em] text-charcoal-500">
                      ~{continueLesson.estimated_minutes} min
                    </p>
                    <PrimaryButton asChild className="mt-5 w-full">
                      <Link
                        to="/academy/tracks/$trackSlug/lessons/$lessonSlug"
                        params={{ trackSlug: track.slug, lessonSlug: continueLesson.slug }}
                      >
                        {completedCount > 0 ? "Resume" : "Start"} <ArrowRight className="h-4 w-4" />
                      </Link>
                    </PrimaryButton>
                  </>
                )}
                {lessons.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center justify-between font-sans text-[11px] uppercase tracking-[0.18em] text-charcoal-500">
                      <span>Progress</span>
                      <span>{completedCount} / {lessons.length}</span>
                    </div>
                    <div className="mt-2 h-1 w-full overflow-hidden bg-bronze-600/15">
                      <div className="h-full bg-bronze-600 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => setAskOpen(true)}
                className="mt-4 flex w-full items-center justify-center gap-2 border border-bronze-600/30 bg-cream-50 px-5 py-3 font-sans text-[12px] font-semibold uppercase tracking-[0.18em] text-navy-900 transition-colors hover:bg-white"
                style={{ borderRadius: 4 }}
              >
                <MessageCircleQuestion className="h-4 w-4 text-bronze-600" />
                Ask about this track
              </button>
            </aside>
          </div>
        </section>

        {related.length > 0 && (
          <section className="bg-white py-16">
            <div className="mx-auto max-w-7xl px-5 md:px-10">
              <div className="text-center"><Eyebrow>Other Tracks</Eyebrow></div>
              <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {related.map((t) => <TrackCard key={t.id} track={t} />)}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
      <AskQuestionModal
        open={askOpen}
        onClose={() => setAskOpen(false)}
        contextLabel={`${track.title} track`}
      />
    </div>
  );
}